import React, { useState, useEffect } from 'react';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import NeedAuthModal from '../Modals/NeedAuthModal';
import GuestBookingModal from '../Modals/GuestInformationModal';
import AnimatedButton from '../Buttons/AnimatedButton';
import { useLanguage } from '../../context/LanguageContext';
import { slug } from '../../utils/store';

export default function MainSection({ bookDisabled = false, setRegisterModal, isRegister, loginModal, setLoginModal }) {
    const { t, isRTL } = useLanguage();
    const navigate = useNavigate();
    const { data: centerData } = useDoctorQuery();

    const isAuthorized = !!localStorage.getItem(`userTkn-${slug}`) || !!sessionStorage.getItem(`userTkn-${slug}`);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
    const [pendingRoute, setPendingRoute] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isAppoinments, setIsAppoinments] = useState(false);

    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    if (!centerData) return null;

    const centerName = centerData?.name || t('mainSection.defaultCenterName');

    // --- Animation Variants (unchanged) ---
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.2 } },
    };

    const heroVariants = {
        hidden: { opacity: 0, scale: 0.95, y: 30 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 1.2, ease: [0.25, 0.25, 0.25, 0.75] } },
    };

    const textRevealVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: (i = 1) => ({
            opacity: 1, y: 0,
            transition: { delay: i * 0.15, duration: 0.8, ease: [0.25, 0.25, 0.25, 0.75] },
        }),
    };

    const buttonVariants = {
        hidden: { opacity: 0, y: 20, scale: 0.95 },
        visible: (i = 1) => ({
            opacity: 1, y: 0, scale: 1,
            transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
        }),
        hover: { scale: 1.05, y: -2, transition: { duration: 0.3 } },
        tap: { scale: 0.98 },
    };

    const serviceCardVariants = {
        hidden: { opacity: 0, y: 60, rotateX: -15 },
        visible: (i = 1) => ({
            opacity: 1, y: 0, rotateX: 0,
            transition: { delay: i * 0.2, duration: 0.8, ease: [0.25, 0.25, 0.25, 0.75] },
        }),
        hover: { y: -10, rotateX: 5, scale: 1.03, transition: { duration: 0.4, ease: 'easeOut' } },
    };

    const floatingVariants = {
        animate: {
            y: [-10, 10, -10], rotate: [-1, 1, -1],
            transition: { duration: 6, ease: 'easeInOut', repeat: Infinity },
        },
    };

    const pulseVariants = {
        animate: {
            scale: [1, 1.05, 1], opacity: [0.7, 1, 0.7],
            transition: { duration: 3, ease: 'easeInOut', repeat: Infinity },
        },
    };

    const handleProtectedNavigation = (route ) => {
        if (!isAuthorized) {
            setPendingRoute(route);
            setIsModalVisible(true);
        } else {
            navigate(route);
        }
    };

    const handleGuestBooking = () => {
        setIsModalVisible(false);
        setIsGuestModalVisible(true);
    };

    const handleGuestInfoSubmit = (guestInfo) => {
        setIsGuestModalVisible(false);
        sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
        sessionStorage.setItem(`isGuestBooking-${slug}`, 'true');
        navigate(pendingRoute || `/book-appointment`);
    };

    const services = [
        {
            icon: (
                <svg className="w-6 h-6 sm:w-8 sm:h-8 goldy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            ),
            title: t('mainSection.services.heartRate.title'),
            description: t('mainSection.services.heartRate.description'),
        },
        {
            icon: (
                <svg className="w-6 h-6 sm:w-8 sm:h-8 goldy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            title: t('mainSection.services.counseling.title'),
            description: t('mainSection.services.counseling.description'),
        },
        {
            icon: (
                 <svg className="w-6 h-6 sm:w-8 sm:h-8 goldy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
            ),
            title: t('mainSection.services.doctors.title'),
            description: t('mainSection.services.doctors.description'),
        },
        {
            icon: (
              <svg className="w-6 h-6 sm:w-8 sm:h-8 goldy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            title: t('mainSection.services.emergency.title'),
            description: t('mainSection.services.emergency.description'),
        },
    ];

    return (
        <>
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute -top-1/4 -left-1/4 w-48 h-48 lg:w-96 lg:h-96 bg-primary/20 rounded-full blur-3xl"
                    animate={{ x: [0, 100, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                />
                <motion.div
                    className="absolute -bottom-1/4 -right-1/4 w-48 h-48 lg:w-96 lg:h-96 bg-secondary/20 rounded-full blur-3xl"
                    animate={{ x: [0, -100, 0], y: [0, 50, 0], scale: [1.2, 1, 1.2] }}
                    transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                />
            </div>

            {/* Hero Section */}
            <motion.div
                id="about"
                className="relative w-full overflow-hidden"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                {/* Gradient background */}
             <motion.div
    className="absolute inset-0"
    animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
    transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
    style={{
        background: 'linear-gradient(135deg, #C9A84C, #C9A84C, #EDD97A, #A8872E, #7A5C1E)',
        backgroundSize: '300% 300%',
    }}
/>

                {/* Hero image — hidden on mobile, visible md+ */}
  {/* Desktop hero image — remove ALL margin classes, use inline style for flip */}
<motion.div
    className={`absolute top-0 h-full w-auto hidden md:block ${isRTL ? 'left-0' : 'right-0'}`}
    variants={floatingVariants}
    animate="animate"
>
    <img
        src="/images/hero.png"
        alt={t('mainSection.imageAlt')}
        className="h-full w-auto object-contain opacity-80 p-12 mt-20 scale-90"
        style={{
            transform: isRTL ? 'scaleX(-1)' : 'none',
        }}
        loading="lazy"
    />
    <motion.div
        className={`absolute inset-0 blur-xl ${isRTL ? 'bg-gradient-to-r' : 'bg-gradient-to-l'} from-cyan-400/20 to-transparent`}
        variants={pulseVariants}
        animate="animate"
    />
</motion.div>
                {/* Mouse parallax — desktop only */}
                <motion.div
                    className="absolute inset-0 hidden lg:block"
                    style={{
                        background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.1) 0%, transparent 50%)`,
                    }}
                />

                {/* ── MOBILE hero image (sits between gradient and content, below text) ── */}
                <div className="relative flex flex-col md:hidden">
                    {/* Text content */}
                       {/* Mobile hero image — in-flow, centered */}
                    <motion.div
                        className={`absolute ${isRTL ? 'left-0 mt-6' : ' mt-6 right-0'} z-10 flex justify-center px-8 py-2`}
                        variants={floatingVariants}
                        animate="animate"
                    >
                        <img
                            src="/images/hero.png"
                            alt={t('mainSection.imageAlt')}
                            className="w-52 h-auto object-contain opacity-90 drop-shadow-2xl"
                            loading="lazy"
                        />
                    </motion.div>
                    <div className="relative z-10 px-5 pt-12 pb-4">
                        <motion.div variants={heroVariants} className="space-y-4">
                            <motion.h1
                                className="text-3xl font-bold leading-tight"
                                variants={textRevealVariants}
                                custom={1}
                            >
                                <motion.span className="text-white block">{centerName}</motion.span>
                                <motion.span className="text-white drop-shadow-lg" variants={textRevealVariants} custom={2}>
                                    {t('mainSection.subtitle')}
                                </motion.span>
                            </motion.h1>
                            

                            <motion.div variants={textRevealVariants} custom={3} className="relative">
                                <motion.p className="text-sm text-white leading-relaxed backdrop-blur-sm bg-primary/40 px-4 py-3 rounded-xl border border-white/20">
                                    {t('mainSection.description')}
                                </motion.p>
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-primary/20 rounded-xl blur-xl -z-10"
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                />
                            </motion.div>
                        </motion.div>
                    </div>

                 

                    {/* Mobile buttons */}
                    <div className="relative z-10 px-5 pb-12 flex flex-col gap-3">
                        <AnimatedButton
                            primary
                            disabled={bookDisabled || centerData.isLocked}
                            onClick={() => {handleProtectedNavigation(`/book-appointment`);setIsAppoinments(false)}}
                        >
                            {t('mainSection.buttons.getTreatment')}
                        </AnimatedButton>
                        <AnimatedButton
    primary
    icon={true}
    disabled={bookDisabled}
    onClick={() => { handleProtectedNavigation(`/my-appointments`); setIsAppoinments(true); }}
>
    {t('mainSection.buttons.appointments')}
</AnimatedButton>
                    </div>
                </div>

                <div className="relative hidden md:flex items-center md:px-20 min-h-[600px] lg:min-h-[700px] py-16 lg:py-24">
                    <div className="max-w-[1670px] mx-auto px-6 lg:px-8 w-full">
                        <div className="max-w-3xl">
                            <motion.div variants={heroVariants} className="space-y-6 lg:space-y-8">
                                <motion.h1
                                    className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight"
                                    variants={textRevealVariants}
                                    custom={1}
                                >
                                    <motion.span className="text-white block">{centerName}</motion.span>
                                    <motion.span className="text-white drop-shadow-lg" variants={textRevealVariants} custom={2}>
                                        {t('mainSection.subtitle')}
                                    </motion.span>
                                </motion.h1>

                                <motion.div variants={textRevealVariants} custom={3} className="relative">
                                    <motion.p
                                        className="text-base md:text-lg lg:text-xl text-white leading-relaxed backdrop-blur-sm bg-white/10 p-6 rounded-2xl border border-white/20"
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {t('mainSection.description')}
                                    </motion.p>
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-primary/20 rounded-2xl blur-xl -z-10"
                                        animate={{ opacity: [0.5, 1, 0.5] }}
                                        transition={{ duration: 3, repeat: Infinity }}
                                    />
                                </motion.div>
<motion.div className="flex flex-row gap-4 pt-4" variants={containerVariants}>
    <AnimatedButton
        primary
        disabled={bookDisabled || centerData.isLocked}
        onClick={() => handleProtectedNavigation(`/book-appointment`)}
    >
        {t('mainSection.buttons.getTreatment')}
    </AnimatedButton>

    <AnimatedButton
    primary
    icon={true}
        disabled={bookDisabled}
        onClick={() => { handleProtectedNavigation(`/my-appointments`); setIsAppoinments(true); }}
    >
        {t('mainSection.buttons.appointments')}
    </AnimatedButton>
</motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator — md+ only */}
                <motion.div
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                        <motion.div
                            className="w-1 h-3 bg-white rounded-full mt-2"
                            animate={{ y: [0, 12, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                    </div>
                </motion.div>
            </motion.div>

            {/* Services Section */}
            <motion.div
                className="bg-cardColor py-8 sm:py-12 lg:py-16 mb-12"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 text-center">
                        {services.map((service, index) => (
                            <motion.div
                                key={service.title}
                                className="p-3 sm:p-4"
                                variants={serviceCardVariants}
                                initial="hidden"
                                animate="visible"
                                whileHover="hover"
                                custom={index}
                            >
                                <div className="mb-3 sm:mb-4 flex justify-center">
                                    <motion.div
                                        className="w-10 h-10 sm:w-16 sm:h-16 text-primary bg-secondary/20 rounded-full flex items-center justify-center backdrop-blur-sm"
                                        whileHover={{ rotate: 360, scale: 1.1 }}
                                        transition={{ duration: 0.6 }}
                                    >
                                        {service.icon}
                                    </motion.div>
                                </div>
                                <h3 className="text-sm sm:text-xl text-primary font-bold mb-1 sm:mb-2 leading-tight">
                                    {service.title}
                                </h3>
                                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed hidden sm:block">
                                    {service.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            <NeedAuthModal
                open={isModalVisible}
                onConfirm={() => { setIsModalVisible(false); setLoginModal(true); }}
                onCancel={() => { setIsModalVisible(false); setPendingRoute(null);setIsAppoinments(false) }}
                onGuestBooking={handleGuestBooking}
                isAppoinments={isAppoinments}
                
            />
            <GuestBookingModal
                open={isGuestModalVisible}
                onCancel={() => { setIsGuestModalVisible(false); setPendingRoute(null); }}
                onSubmit={handleGuestInfoSubmit}
            />
        </>
    );
}